import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { ICONS } from '../icons';
import { DIVIDERS, DividerId, SCRIMS, ScrimId, SHAPES, ShapeId } from '../shapes';
import type { MaskKind } from '../types';
import type { DesignEditorApi } from '../useDesignEditor';
import { SectionLabel, Tile } from '../ui';

/** Line-art glyphs for the shape tiles — the shape itself, not a metaphor. */
const ShapeGlyph: React.FC<{ id: ShapeId }> = ({ id }) => {
    const common = { fill: 'currentColor', stroke: 'none' } as const;
    const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const };
    return (
        <svg viewBox="0 0 48 48" className="size-9">
            {id === 'rect' && <rect x="6" y="12" width="36" height="24" {...common} opacity={0.85} />}
            {id === 'rounded' && <rect x="6" y="12" width="36" height="24" rx="7" {...common} opacity={0.85} />}
            {id === 'circle' && <circle cx="24" cy="24" r="15" {...common} opacity={0.85} />}
            {id === 'ellipse' && <ellipse cx="24" cy="24" rx="19" ry="12" {...common} opacity={0.85} />}
            {id === 'triangle' && <polygon points="24,8 41,38 7,38" {...common} opacity={0.85} />}
            {id === 'polygon' && <polygon points="24,7 39,15.5 39,32.5 24,41 9,32.5 9,15.5" {...common} opacity={0.85} />}
            {id === 'star' && <polygon points="24,6 28.6,18.2 41.6,18.6 31.4,26.6 35,39.2 24,31.8 13,39.2 16.6,26.6 6.4,18.6 19.4,18.2" {...common} opacity={0.85} />}
            {id === 'burst' && (
                <polygon
                    points={Array.from({ length: 32 }, (_, i) => {
                        const r = i % 2 === 0 ? 17 : 14;
                        const a = -Math.PI / 2 + (i * Math.PI) / 16;
                        return `${24 + r * Math.cos(a)},${24 + r * Math.sin(a)}`;
                    }).join(' ')}
                    {...common}
                    opacity={0.85}
                />
            )}
            {id === 'frame' && <rect x="12" y="7" width="24" height="34" {...line} />}
            {id === 'line' && <line x1="6" y1="24" x2="42" y2="24" {...line} />}
            {id === 'dashed' && <line x1="6" y1="24" x2="42" y2="24" {...line} strokeDasharray="5 4" strokeLinecap="butt" />}
            {id === 'arrow' && <path d="M6 24 H41 M33 16 L41 24 L33 32" {...line} strokeLinejoin="round" />}
        </svg>
    );
};

const FrameGlyph: React.FC<{ kind: MaskKind }> = ({ kind }) => (
    <svg viewBox="0 0 48 48" className="size-9">
        <defs>
            <linearGradient id={`fg-${kind}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="currentColor" stopOpacity="0.35" />
                <stop offset="1" stopColor="currentColor" stopOpacity="0.8" />
            </linearGradient>
        </defs>
        {kind === 'none' && <rect x="9" y="9" width="30" height="30" fill={`url(#fg-${kind})`} />}
        {kind === 'rounded' && <rect x="9" y="9" width="30" height="30" rx="7" fill={`url(#fg-${kind})`} />}
        {kind === 'circle' && <circle cx="24" cy="24" r="15" fill={`url(#fg-${kind})`} />}
        {kind === 'arch' && <path d="M12 42 V22 A12 12 0 0 1 36 22 V42 Z" fill={`url(#fg-${kind})`} />}
    </svg>
);

const ScrimGlyph: React.FC<{ id: ScrimId }> = ({ id }) => {
    const bg: Record<ScrimId, string> = {
        'scrim-bottom': 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.75))',
        'scrim-top': 'linear-gradient(0deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.7))',
        'scrim-left': 'linear-gradient(270deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.7))',
        overlay: 'rgba(0,0,0,0.38)',
        vignette: 'radial-gradient(circle, rgba(0,0,0,0) 35%, rgba(0,0,0,0.6))',
    };
    return (
        <span className="relative block h-11 w-full overflow-hidden rounded-md bg-[linear-gradient(135deg,#D9CFC0,#A89A86)]">
            <span className="absolute inset-0" style={{ background: bg[id] }} />
        </span>
    );
};

const DividerGlyph: React.FC<{ id: DividerId }> = ({ id }) => (
    <svg viewBox="0 0 64 24" className="h-6 w-16" fill="none" stroke="currentColor" strokeWidth={1.5}>
        {id === 'rule' && <line x1="4" y1="12" x2="60" y2="12" />}
        {id === 'short' && <line x1="24" y1="12" x2="40" y2="12" strokeWidth={2.5} />}
        {id === 'double' && <><line x1="4" y1="10" x2="60" y2="10" /><line x1="4" y1="14" x2="60" y2="14" /></>}
        {id === 'dot' && <><line x1="4" y1="12" x2="26" y2="12" /><circle cx="32" cy="12" r="2" fill="currentColor" /><line x1="38" y1="12" x2="60" y2="12" /></>}
    </svg>
);

export const ElementsPanel: React.FC<{ ed: DesignEditorApi; primaryImage?: string | null }> = ({ ed, primaryImage }) => {
    const { t } = useTranslation();
    const [q, setQ] = useState('');
    const icons = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return ICONS;
        return ICONS.filter((i) => i.id.includes(s) || i.tags.includes(s) || t(`DESIGN_EDITOR.ICON_${i.id.toUpperCase().replace(/-/g, '_')}`).toLowerCase().includes(s));
    }, [q, t]);

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.SHAPES')}</SectionLabel>
                <div className="grid grid-cols-4 gap-1.5">
                    {SHAPES.map((id) => (
                        <Tile key={id} label={t(`DESIGN_EDITOR.SHAPE_${id.toUpperCase()}`)} onClick={() => ed.addShape(id)} className="aspect-square p-1">
                            <ShapeGlyph id={id} />
                        </Tile>
                    ))}
                </div>
            </div>

            {primaryImage && (
                <div className="flex flex-col gap-2">
                    <SectionLabel>{t('DESIGN_EDITOR.FRAMES')}</SectionLabel>
                    <div className="grid grid-cols-4 gap-1.5">
                        {(['none', 'rounded', 'circle', 'arch'] as MaskKind[]).map((k) => (
                            <Tile
                                key={k}
                                label={t(`DESIGN_EDITOR.MASK_${k.toUpperCase()}`)}
                                onClick={() => { void ed.addImage(primaryImage, { mask: k, asFrame: true }); }}
                                className="aspect-square p-1"
                            >
                                <FrameGlyph kind={k} />
                            </Tile>
                        ))}
                    </div>
                    <p className="text-[11px] leading-snug text-muted-foreground">{t('DESIGN_EDITOR.FRAMES_HINT')}</p>
                </div>
            )}

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.SCRIMS')}</SectionLabel>
                <div className="grid grid-cols-3 gap-1.5">
                    {SCRIMS.map((id) => (
                        <Tile key={id} label={t(`DESIGN_EDITOR.SCRIM_${id.toUpperCase().replace(/-/g, '_')}`)} onClick={() => ed.addScrim(id)} showLabel className="p-1.5">
                            <ScrimGlyph id={id} />
                        </Tile>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.DIVIDERS')}</SectionLabel>
                <div className="grid grid-cols-2 gap-1.5">
                    {DIVIDERS.map((id) => (
                        <Tile key={id} label={t(`DESIGN_EDITOR.DIVIDER_${id.toUpperCase()}`)} onClick={() => ed.addDivider(id)} className="h-12">
                            <DividerGlyph id={id} />
                        </Tile>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <SectionLabel>{t('DESIGN_EDITOR.ICONS')}</SectionLabel>
                <div className="flex h-8 items-center gap-2 rounded-lg border bg-background px-2.5">
                    <Search className="size-3.5 text-muted-foreground" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t('DESIGN_EDITOR.ICON_SEARCH')}
                        className="w-full border-none bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
                    />
                </div>
                <div className="grid grid-cols-6 gap-1">
                    {icons.map(({ id, Icon }) => (
                        <Tile key={id} label={t(`DESIGN_EDITOR.ICON_${id.toUpperCase().replace(/-/g, '_')}`)} onClick={() => { void ed.addIcon(id); }} className="aspect-square rounded-lg p-0">
                            <Icon className="size-[18px]" strokeWidth={1.6} />
                        </Tile>
                    ))}
                </div>
                {icons.length === 0 && <div className="py-3 text-center text-xs text-muted-foreground">{t('DESIGN_EDITOR.NOTHING_FOUND')}</div>}
            </div>
        </div>
    );
};
